// Recent-orders persistence for the no-backend reorder surfaces.
//
// We keep up to 3 of the customer's most recent orders (most-recent-first,
// de-duplicated by content) so the hero "Reorder last delivery" drawer can
// offer a choice between their standard loads — not just the single latest
// order.
//
// IMPORTANT: this uses the `SavedOrder` shape (the reorder payload written
// by the cart), which is DIFFERENT from the `TrackedOrder` shape in
// utils/orderTracking.ts. They are not interchangeable.
//
// The legacy single-order key ('253gravel_last_order') is left untouched —
// other surfaces and older devices still read it. pushRecentOrder is purely
// additive, and getRecentOrders seeds from the legacy key on first read so
// existing customers immediately see their last order in the new drawer.

export type SavedOrderItem = {
  id?: string
  category: string
  product: string
  quantity: number
  pricePerYard: number
}

export type SavedOrder = {
  items: SavedOrderItem[]
  addressInput?: string
  name?: string
  phone?: string
  email?: string
}

const RECENT_ORDERS_KEY = '253gravel_recent_orders'
const LEGACY_LAST_ORDER_KEY = '253gravel_last_order'
const MAX_RECENT = 3

// Stable content signature used to de-duplicate. Two orders are "the same"
// if they request the same materials/quantities to the same address.
function orderSignature(order: SavedOrder): string {
  try {
    const items = (order.items || [])
      .map((i) => `${i.category}|${i.product}|${i.quantity}`)
      .sort()
      .join('~')
    return `${items}@@${(order.addressInput || '').trim().toLowerCase()}`
  } catch {
    return Math.random().toString(36)
  }
}

function isValidOrder(o: unknown): o is SavedOrder {
  return (
    !!o &&
    typeof o === 'object' &&
    Array.isArray((o as SavedOrder).items) &&
    (o as SavedOrder).items.length > 0
  )
}

function readLegacyLastOrder(): SavedOrder | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(LEGACY_LAST_ORDER_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return isValidOrder(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeRecent(orders: SavedOrder[]) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(
      RECENT_ORDERS_KEY,
      JSON.stringify(orders.slice(0, MAX_RECENT)),
    )
  } catch {
    /* private window / quota — ignore */
  }
}

// Returns up to 3 recent orders, most-recent-first. If the new array key is
// empty (existing customer who ordered before this feature shipped), seed it
// from the legacy single-order key so they still see their last load.
export function getRecentOrders(): SavedOrder[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(RECENT_ORDERS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        const valid = parsed.filter(isValidOrder)
        if (valid.length > 0) return valid.slice(0, MAX_RECENT)
      }
    }
  } catch {
    /* fall through to legacy seed */
  }
  const legacy = readLegacyLastOrder()
  return legacy ? [legacy] : []
}

// Prepend a freshly placed order to the recent list: de-dupe by content,
// cap at 3, most-recent-first. Additive — does not touch the legacy key.
export function pushRecentOrder(order: SavedOrder): void {
  if (typeof window === 'undefined' || !isValidOrder(order)) return
  const existing = getRecentOrders()
  const sig = orderSignature(order)
  const deduped = existing.filter((o) => orderSignature(o) !== sig)
  writeRecent([order, ...deduped])
}

// Shared display helper so every reorder surface labels an order identically.
export function summarizeOrder(order: SavedOrder): string {
  const items = order.items || []
  if (items.length === 0) return 'Saved order'
  if (items.length > 1) {
    const totalYards = items.reduce((s, i) => s + i.quantity, 0)
    return `${totalYards} yds · ${items.length} materials`
  }
  return `${items[0].quantity} yds ${items[0].product}`
}
